import { AsYouType } from "libphonenumber-js";
import getSvgs from "./messageSvgs";
import {
  apiResult,
  Contact,
  ContactsDBObject,
  Escalation,
  Message,
  FilterOption,
} from "../interfaces";
import {
  getTimeStampForContact,
  getTimeStampForMessage,
  getTimeStampForMessageGrouping,
  getTimeStampForLeads,
} from "./moment";

const availableLanguages = ["en", "es", "pt"]; // make this global at some point

const createEmptyTranslation = () =>
  Object.fromEntries(availableLanguages.map((lang) => [lang, ""]));

function filterContact(
  contactObj: ContactsDBObject[string],
  filters: FilterOption[]
) {
  try {
    const { contact, mine } = contactObj;
    const activeFilters = filters.filter((f) => f.isActive);

    // Hide archived contacts for main view unless explicitly filtered
    const isArchived = filters.find(
      (f) => f.label === "bucket" && f.value === "archived"
    );
    if (!contact.isSearch && !isArchived && contact.archive) {
      return false;
    }

    const conditions = [];
    for (const filter of activeFilters) {
      switch (filter.label) {
        case "companies":
          if (!(filter.value as string[]).includes(contact.companies[0]?.id)) {
            return false;
          }
          break;
        case "users":
          if (
            !contact.assignations?.some((a) =>
              (filter.value as string[]).includes(a.id)
            )
          ) {
            return false;
          }
          break;
        case "bucket":
          if (
            filter.value === "pending" &&
            !(contact.escalated || (contact.unread && !contact.enabled))
          ) {
            return false;
          }
          /*
          not used
          if (filter.value === "mine" && !mine) {
            return false;
          }
          */
          if (filter.value === "archived" && !contact.archive) {
            return false;
          }
          break;
        case "search":
          if (!contact.isSearch) {
            return false;
          }
          break;
        case "quality":
          if (!(filter.value as string[]).includes(contact.score as string)) {
            return false;
          }
          break;
        case "actions":
          if (
            !contact.actionsList ||
            !contact.actionsList.some((action) =>
              (filter.value as string[]).includes(action)
            )
          ) {
            return false;
          }
          break;
      }
      if (filter.condition === "or") {
        switch (filter.type) {
          case "toggle":
            conditions.push(
              contact[filter.label as keyof Contact] === filter.value
            );
            break;
        }
      }
    }
    const flag = true;
    return conditions.length ? conditions.some((c) => c) : flag;
  } catch (e) {
    console.error("Error filtering contact:", e);
    return true;
  }
}

export function runChatList(
  contacts: ContactsDBObject,
  options: {
    filters: FilterOption[];
  }
) {
  const filters = options.filters || [];
  const list: Contact[] = [];
  Object.values(contacts).forEach((value) => {
    const shouldInclude = filterContact(value, filters);
    if (shouldInclude) {
      list.push(value.contact);
    }
  });
  list.sort((a, b) => b.timestampNative - a.timestampNative);
  return { list };
}

export function getContactType(contact: apiResult): string {
  const typeMap: { [key: string]: string } = {
    igId: "ig",
    fmId: "fm",
    calryId: "ab",
    phoneNumberId: "wa",
    webId: "web",
    callId: "voice",
  };

  for (const key in typeMap) {
    if (contact[key]) {
      return typeMap[key];
    }
  }

  return "play";
}

function mapCompanies(companies: apiResult[]) {
  return companies?.map((company) => ({
    name: company.name || "",
    id: company._id || "",
  }));
}

function mapAssignation(assignations: apiResult[]) {
  return assignations?.map((assignation) => ({
    email: assignation.email || "",
    id: assignation._id || "",
  }));
}

export function mapMessages(messages: apiResult[]) {
  function buildMessage(message: apiResult): apiResult {
    return {
      id: message._id || "",
      content: message.content || "",
      right: message.role === "assistant" || message.role === "outbound",
      status: message.status || "",
      escalated: Boolean(message.escalated),
      timestamp: getTimeStampForMessage(message.createAt),
      timestampGrouping: getTimeStampForMessageGrouping(message.createAt),
      createdAt: message.createAt,
      svg: getSvgs(message.status),
      interactive: {
        flag: Boolean(message.interactive),
        data: message.interactive || null,
      },
      replyTo: message.meta?.replyTo
        ? buildMessage(message.meta?.replyTo)
        : null,
      isFollowUp: Boolean(message.meta?.isFollowUp),
      isManual: Boolean(message.meta?.user),
      email: message.meta?.user || "",
      error: message.meta?.error || "",
      reactions:
        message.meta?.reactions?.map((r: apiResult) => ({
          emoji: r.emoji,
          from: r.from,
        })) || [],
      flag: message.flag ? { ...message.flag } : null,
      isAd: message.meta?.referral?.url ? true : false,
      adUrl: message.meta?.referral?.url || "",
    };
  }

  return messages.map((m) => buildMessage(m));
}

function mapBookings(bookings: apiResult[]) {
  return bookings.map((b) => ({
    phoneNumber: b.phoneNumber || "",
    status: b.status || "",
    externalId: b.externalId || "",
    fullName: b.fullName || "",
    from: b.from || "",
    to: b.to || "",
    locationName: b.locationName || "",
    rooms: b.rooms || [],
    conversion: Boolean(b.conversion),
  }));
}

function mapEscalations(escalations: apiResult[], type: string) {
  return escalations
    .map((e) => ({
      type: e.type || "",
      open: Boolean(e.open),
      details: e.details || "",
      escalated: e.open && type !== "play",
      message: e?.message,
      createdAt: e.createdAt || e.createAt,
    }))
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
}

function mapToolCalls(toolCalls: apiResult[]) {
  return toolCalls.map((t) => ({
    id: t._id || "",
    createdAt: t.createdAt,
    name: t?.name,
    content: t?.content,
    type: t.type,
    arguments: t.arguments,
    timestamp: getTimeStampForMessage(t.createdAt),
    timestampGrouping: getTimeStampForMessageGrouping(t.creadteAt),
  }));
}

export function contactAdapter(c: apiResult = {}) {
  const isManual = c.ai?.isManual || false;
  const isBook = c.ai?.isBook || false;
  const isFlagged = c.flagged || false;
  const archive = c?.archive || false;
  const type = getContactType(c);
  const followups =
    c.followups?.map((f: apiResult) => ({
      followedUp: f.followedUp,
      rejected: f.rejected,
    })) || [];

  const score = c.lead?.score || 0;
  // pending count how many not rejected not followed up
  const contact = {
    name: c.fullName,
    queryString: c.queryString || "",
    timestamp: getTimeStampForContact(c.updateAt),
    timestampHour: getTimeStampForLeads(c.updateAt),
    timestampNative: new Date(c.updateAt).getTime(),
    preview: c.messagePreview || "",
    id: c._id,
    unread: c.isUnread ? 1 : 0,
    enabled: c.enabled,
    manual: !c.enabled,
    optedOut: c.optedOut || false,
    phoneNumber: c.phoneNumber
      ? new AsYouType().input(`+${c.phoneNumber}`)
      : "",
    status: c.status || "",
    archive,
    blocked: Boolean(c.blocked),
    type,
    followers: c.meta?.followerCount || 0,
    username: c.meta?.username || "",
    igFmPageSource: c.meta?.igFmPageName,
    isGuest: Boolean(c.bookings?.length),
    isBook,
    isManual,
    isOnline: c.isOnline || false,
    escalated: Boolean(c.escalated),
    isConversion: false,
    isFlagged,
    actionsList: c.actionsList || [],
    score:
      score === 0
        ? "no_score"
        : score === 1
        ? "low"
        : score <= 3
        ? "medium"
        : "high",
    scoreExplanation: c.lead?.description || createEmptyTranslation(),
    intent: c.classification?.intent || createEmptyTranslation(),
    intentDescription:
      c.classification?.description || createEmptyTranslation(),
    followUpStatus: followups.length
      ? followups[followups.length - 1].followedUp
        ? "followed_up"
        : followups[followups.length - 1].rejected
        ? "rejected"
        : "pending"
      : "no_follow_up",
    successfulFollowUps: followups.filter((f: apiResult) => f.followedUp)
      .length,
  };
  return contact as Contact;
}

export function adaptItem(c: apiResult) {
  const contact = contactAdapter(c);
  const companies = mapCompanies(c.companies);
  const assignations = mapAssignation(c.assignations);
  const messages = c.messages ? (mapMessages(c.messages) as Message[]) : [];
  const bookings = c.bookings ? mapBookings(c.bookings) : [];
  const escalations = c.escalations
    ? (mapEscalations(c.escalations, contact.type) as Escalation[])
    : [];
  const toolCalls = c.toolCalls ? mapToolCalls(c.toolCalls) : [];
  return {
    contact: { ...contact, companies, assignations },
    messages,
    bookings,
    escalations,
    companies,
    toolCalls,
    assignations,
  };
}

type Grouped = Record<
  string,
  Array<{ kind: "message" | "toolCall"; item: any }>
>;

const ts = (x: any) => new Date(x?.createdAt).getTime();

/**
 * Messages arrive already sorted from backend (keep their relative order).
 * We only insert tool calls into the correct position based on timestamp.
 */
export const groupMessages = (messages: any[] = [], toolCalls: any[] = []) => {
  // 1) Group messages in their given order
  const groups: Grouped = messages.reduce((acc, m) => {
    const key = m.timestampGrouping || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push({ kind: "message", item: m });
    return acc;
  }, {} as Grouped);

  // 2) Insert each toolCall into the right group and position
  for (const t of toolCalls) {
    const key = t.timestampGrouping || "Unknown";
    if (!groups[key]) groups[key] = [];

    const tTime = ts(t);

    // Find first message with timestamp >= tool timestamp, insert before it.
    // This preserves original message order (stable), only tool calls move.
    const idx = groups[key].findIndex(
      (e) => e.kind === "message" && ts(e.item) >= tTime
    );

    const entry = { kind: "toolCall" as const, item: t };

    if (idx === -1) {
      groups[key].push(entry);
    } else {
      groups[key].splice(idx, 0, entry);
    }
  }
  return groups;
};
